import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import translate from '../translate';
import Form from './Form';
import FormInput from './FormInput';
import TermsAndPolicyCheckbox from './TermsAndPolicyCheckbox';
import Request from '../../services/request';
import Notifications from '../../services/notifications';
import CountryProp from './Address/CountryProp';
import TenantProp from '../propTypes/tenantProp';
import { compose } from '../../services/utilityFunctions';
import withCSRF from '../withCSRF';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import CurrencyProp from '../../sections/profile/propTypes/currencyProp';
import FormUserTypeInput from './FormUserTypeInput';
import MarketInfosContext from '../../contextProviders/MarketInfosContext';

// State key -> DOM element id for the fields a browser/password-manager may
// autofill. Autofill writes straight to the DOM and often does not fire React's
// onChange, so we read these elements back from the DOM to keep the controlled
// state in sync. This covers the text inputs *and* the country/currency <select>
// elements: select2 hides the native <select> and only reports user picks (not
// autofill) back to React, yet the native element still holds the code we must
// send to the API (its <option value> is the country/currency code).
// (BRAVON-6271 — registration blocked on the Bio page when using autofill.)
const AUTOFILL_FIELD_IDS = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  password: 'password',
  confirmPassword: 'confirm_password',
  shippingCountry: 'shippingCountry',
  currency: 'currency',
};

// Reads the current (possibly autofilled) values straight from the DOM elements
// (inputs and the native <select> behind select2), keeping only the non-empty
// ones. Called both when autofill is detected and at submit time (a user gesture,
// which is when browsers expose autofilled password values to JavaScript).
function readAutofilledValues() {
  return Object.entries(AUTOFILL_FIELD_IDS).reduce((acc, [key, id]) => {
    const el = typeof document !== 'undefined' && document.getElementById(id);
    if (el && typeof el.value === 'string' && el.value !== '') {
      acc[key] = el.value;
    }
    return acc;
  }, {});
}

function EnrollForm(props) {
  const {
    t,
    _csrf,
    sponsoredBy,
    onComplete,
    isPublic,
    languages,
    organizationIdentifier,
    onLoginModalOpen,
    tenant,
    title,
    buttonTxt,
    loginTxt,
    currencies,
    countries,
    isFromSession,
  } = props;
  const { loggedUser } = useContext(LoggedUserContext);
  const { marketInfos } = useContext(MarketInfosContext);

  const showShippingCountry =
    (isFromSession && !marketInfos.localCountry) || !isFromSession;
  const showCurrency =
    (isFromSession && !marketInfos.localCurrency) || !isFromSession;

  const [checkbox, setCheckbox] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    language: 'en',
    shippingCountry: loggedUser?.shippingCountry || '',
    currency: loggedUser?.currency || '',
    type: 'customer',
  });
  const {
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    language,
    currency,
    shippingCountry,
    type,
  } = form;

  const handleForm = useCallback(
    field => value => setForm(prevForm => ({ ...prevForm, [field]: value })),
    [setForm],
  );

  // Fired (via the `onAutoFillStart` CSS animation) when the browser autofills an
  // input. Syncs that field's autofilled value into the controlled state so the
  // submit payload isn't left empty. Password values may still be hidden from JS
  // at this point — those are picked up by the submit-time read in onValidated.
  const handleAutoFill = useCallback(({ animationName, target }) => {
    if (animationName !== 'onAutoFillStart' || !target || !target.id) {
      return;
    }
    const field = Object.keys(AUTOFILL_FIELD_IDS).find(
      key => AUTOFILL_FIELD_IDS[key] === target.id,
    );
    const { value } = target;
    if (!field || !value) {
      return;
    }
    setForm(prevForm =>
      prevForm[field] === value ? prevForm : { ...prevForm, [field]: value },
    );
  }, []);

  const handleCheckbox = () => {
    setCheckbox(!checkbox);
  };

  const validationRules = {
    firstName: { required: true },
    lastName: { required: true },
    email: { required: true, email: true },
    password: { required: true, minlength: 6 },
    language: { required: true },
    confirm_password: { required: true, equalTo: '#password' },
  };

  const submitButtonClasses = cx(
    'btn',
    'btn-dynamic',
    'm-btn',
    'm-btn--pill',
    'm-btn--custom',
    'm-btn--air',
    'm-login__btn',
    { 'm-loader': isLoading },
    { 'm-loader--right': isLoading },
    { 'm-loader--light': isLoading },
  );

  const onLoggedIn = () => {
    setIsLoading(true);
    const params = {
      email,
      password,
      organizationIdentifier,
    };
    new Request(_csrf)
      .post('/signin/xhr', params)
      .done(async () => {
        await onComplete(true);
      })
      .fail(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(err);
      });
  };

  const onValidated = () => {
    setIsLoading(true);

    // Final sync from the DOM: captures browser-autofilled values that never
    // fired React's onChange (incl. passwords, which browsers only expose to JS
    // after a user gesture such as this submit click). jQuery validation already
    // checks these DOM inputs, so this keeps the submitted payload consistent
    // with what was validated. (BRAVON-6271)
    const autofilled = readAutofilledValues();
    const resolved = { ...form, ...autofilled };
    setForm(resolved);

    const params = {
      firstName: resolved.firstName,
      lastName: resolved.lastName,
      username: `${resolved.firstName} ${resolved.lastName}`,
      email: resolved.email,
      password: resolved.password,
      language: resolved.language,
      sponsoredBy,
      organizationIdentifier,
      shippingCountry: showShippingCountry
        ? resolved.shippingCountry
        : marketInfos.localCountry,
      type: resolved.type,
      currency: showCurrency ? resolved.currency : marketInfos.localCurrency,
      omitLogin: !!loggedUser?._id,
    };

    new Request(_csrf)
      .post('/signin/signup', params)
      .done(onComplete)
      .fail(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(err);
      });
  };

  const usersCanChooseTheirType = tenant.settings?.usersCanChooseTheirType;
  return (
    <div className="m-content m-portlet session-public-enroll">
      <div className="session-public-enroll-header">
        <h4>{title}</h4>
      </div>
      <div className="session-public-enroll-form">
        <Form
          className="m-form m-form--fit m-form--label-align-right"
          id="sessionSubscribeForm"
          submitMethod={isPublic ? onValidated : onLoggedIn}
          validationRules={validationRules}
          onAnimationStart={handleAutoFill}
        >
          {submitHandler => (
            <>
              {isPublic ? (
                <>
                  {usersCanChooseTheirType && (
                    <FormUserTypeInput
                      onChange={handleForm('type')}
                      value={type}
                    />
                  )}
                  <FormInput
                    id="firstName"
                    name="firstName"
                    type="text"
                    label={t('profile_firstName')}
                    labelClassName="col-lg-3 col-sm-12"
                    inputProps={{ autoComplete: 'new-firstName' }}
                    placeholder={t('profile_firstNamePlaceholder')}
                    value={firstName}
                    onChange={handleForm('firstName')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-9 col-sm-12"
                  />
                  <FormInput
                    id="lastName"
                    name="lastName"
                    type="text"
                    label={t('profile_lastName')}
                    labelClassName="col-lg-3 col-sm-12"
                    inputProps={{ autoComplete: 'new-lastName' }}
                    placeholder={t('profile_lastNamePlaceholder')}
                    value={lastName}
                    onChange={handleForm('lastName')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-9 col-sm-12"
                  />
                  <FormInput
                    id="email"
                    type="email"
                    name="email"
                    label={t('profile_email')}
                    labelClassName="col-lg-3 col-sm-12"
                    inputProps={{ autoComplete: 'new-email' }}
                    placeholder={t('email_holder')}
                    value={email}
                    onChange={handleForm('email')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-9 col-sm-12"
                  />
                  <FormInput
                    id="password"
                    name="password"
                    type="password"
                    label={t('profile_password')}
                    labelClassName="col-lg-3 col-sm-12"
                    inputProps={{ autoComplete: 'new-password' }}
                    placeholder={t('pass_holder')}
                    value={password}
                    onChange={handleForm('password')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-9 col-sm-12"
                  />
                  <FormInput
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    label={t('profile_confirmPassword')}
                    labelClassName="col-lg-3 col-sm-12"
                    inputProps={{ autoComplete: 'new-password' }}
                    placeholder={t('reTypePass_holder')}
                    value={confirmPassword}
                    onChange={handleForm('confirmPassword')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-9 col-sm-12"
                  />

                  {showShippingCountry && (
                    <FormInput
                      id="shippingCountry"
                      name="shippingCountry"
                      type="select"
                      label={t('profile_shippingCountry')}
                      labelClassName="col-lg-3 col-sm-12"
                      placeholder={t('profile_shippingCountry')}
                      value={shippingCountry}
                      onChange={handleForm('shippingCountry')}
                      groupProps={{ className: 'row' }}
                      containerClassName="col-lg-9 col-sm-12"
                      inputProps={{
                        items: countries,
                        autoComplete: 'new-shippingCountry',
                      }}
                    />
                  )}

                  {showCurrency && (
                    <FormInput
                      id="currency"
                      name="currency"
                      type="select"
                      label={t('profile_currency')}
                      labelClassName="col-lg-3 col-sm-12"
                      placeholder={t('profile_currency')}
                      value={currency}
                      onChange={handleForm('currency')}
                      groupProps={{ className: 'row' }}
                      containerClassName="col-lg-9 col-sm-12"
                      inputProps={{
                        items: currencies,
                      }}
                    />
                  )}
                  <div className="row">
                    <span className="col-lg-3 col-sm-12" />
                    <div className="form-group m-form__group select-container">
                      <select
                        className="form-control m-input signup-input"
                        name="language"
                        value={language}
                        onChange={({ currentTarget }) =>
                          handleForm('language')(currentTarget.value)
                        }
                      >
                        {languages.map(l => (
                          <option key={l.code} value={l.code}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="checkbox-terms-and-policy-wrapper">
                    <TermsAndPolicyCheckbox
                      checkboxValue={checkbox}
                      handleCheckbox={handleCheckbox}
                      tenant={tenant}
                    />
                  </div>
                  <div className="login-section">
                    <button
                      id="createAccount"
                      type="submit"
                      className={submitButtonClasses}
                      disabled={!checkbox || isLoading}
                      onClick={submitHandler}
                    >
                      {loginTxt}
                    </button>
                    <span className="login-container">
                      {t('login.alreadyHaveAccount')}
                      <button
                        type="button"
                        onClick={() => onLoginModalOpen(true)}
                      >
                        {buttonTxt}
                      </button>
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <FormInput
                    id="email"
                    type="email"
                    name="email"
                    label={t('profile_email')}
                    labelClassName="col-lg-3 col-sm-12"
                    inputProps={{ autoComplete: 'username' }}
                    placeholder={t('email_holder')}
                    value={email}
                    onChange={handleForm('email')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-9 col-sm-12"
                  />
                  <FormInput
                    id="password"
                    name="password"
                    type="password"
                    label={t('profile_password')}
                    labelClassName="col-lg-3 col-sm-12"
                    inputProps={{ autoComplete: 'current-password' }}
                    placeholder={t('pass_holder')}
                    value={password}
                    onChange={handleForm('password')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-9 col-sm-12"
                  />
                  <div className="login-section">
                    <button
                      id="createAccount"
                      type="submit"
                      className={submitButtonClasses}
                      disabled={isLoading}
                      onClick={submitHandler}
                    >
                      {t('login')}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </Form>
      </div>
    </div>
  );
}

EnrollForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  sponsoredBy: PropTypes.string,
  onComplete: PropTypes.func,
  isPublic: PropTypes.bool.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  organizationIdentifier: PropTypes.string.isRequired,
  onLoginModalOpen: PropTypes.func.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  title: PropTypes.string.isRequired,
  buttonTxt: PropTypes.string.isRequired,
  loginTxt: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  isFromSession: PropTypes.bool,
};

EnrollForm.defaultProps = {
  sponsoredBy: null,
  onComplete: () => {},
  isFromSession: false,
};

export default compose(translate, withCSRF)(EnrollForm);
